// LoginOauthWannabe.container: reads jwt/refresh from URL hash after thpe-auth student OAuth login callback, then routes home.
// Mistake: leaving hash after read can confuse re-renders; replaceState to strip hash once tokens are stored.
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "i18next";

import { setInfo as setUserInfo, setIsLogin } from "reducers/User";
import { getMyInfo } from "api/common/Auth.api";
import * as StudentApi from "api/student/Student.api";
import { setAccessToken, setRefreshToken } from "helpers/Auth.helper";
import { getUserType } from "helpers/Common.helper";
import { getCurrentLanguageCode } from "helpers/String.helper";
import { IUserProperties, setUserProperties } from "helpers/Gtm.helper";

const LoginOauthWannabeContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState<string | null>(null);
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) {
            return;
        }
        ran.current = true;

        const raw = (location.hash || "").replace(/^#/, "");
        if (!raw) {
            setError("missing");
            return;
        }
        const sp = new URLSearchParams(raw);
        const jwt = sp.get("jwt");
        const refresh = sp.get("refresh");
        const needsProfile = sp.get("np") === "1";
        if (!jwt || !refresh) {
            setError("missing");
            return;
        }

        setAccessToken(jwt);
        setRefreshToken(refresh);
        window.history.replaceState(null, "", `${location.pathname}${location.search}`);

        (async () => {
            try {
                if (needsProfile) {
                    dispatch(setIsLogin(true));
                    dispatch(
                        setUserInfo({
                            accountTypeCode: "accountType:student",
                            hasDoneSignup: false,
                        } as any)
                    );
                            navigate("/student/signup", { replace: true });
                    return;
                }

                const myInfoResult = await getMyInfo();
                if (!myInfoResult?.info) {
                    setError("myinfo");
                    return;
                }

                const myInfo = myInfoResult.info;
                if (myInfo.accountTypeCode === "accountType:student" && myInfo.hasDoneSignup === false) {
                    dispatch(setIsLogin(true));
                    dispatch(setUserInfo(myInfo));
                    navigate("/student/signup", { replace: true });
                    return;
                }

                if (myInfo.accountTypeCode === "accountType:student") {
                    const result = await StudentApi.getInfoForStudent();
                    if (result) {
                        myInfoResult.info.profileImage = result.info.profileImageInfo ? result.info.profileImageInfo.url : null;
                    } else {
                        myInfoResult.info.profileImage = null;
                    }
                }

                dispatch(setIsLogin(true));
                dispatch(setUserInfo(myInfo));

                const userProperties: IUserProperties = {
                    user_type: "student",
                    user_type_id: myInfoResult.info.userStudentId,
                    preferred_language: getCurrentLanguageCode(),
                    device_type: "web",
                    device_os: null,
                    device_os_version: null,
                };
                setUserProperties(userProperties);

                const nextUrl =
                    (location.state as { nextUrl?: string } | null)?.nextUrl ||
                    `/${getUserType(myInfo.accountTypeCode)}`;
                const lang = i18n.language === "ko" ? "ko" : "en";
                i18n.changeLanguage(lang);
                navigate(nextUrl, { replace: true });
            } catch (e) {
                console.error("LoginOauthWannabeContainer", e);
                setError("failed");
            }
        })();
    }, [dispatch, location.hash, location.pathname, location.search, location.state, navigate]);

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
                <p className="text-center text-sm text-gray-600">OAuth login could not be completed. Please try again from the login page.</p>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
            <p className="text-sm text-gray-500">Signing you in…</p>
        </div>
    );
};

export default LoginOauthWannabeContainer;
